import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  LabelValue,
  LabelValueSource,
  WbsLabelValueSearchResult
} from 'graphql/types';

import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import {
  determineLabelAcceptedBy,
  usePCPTAutoUpdateHandlers
} from 'modules/timesheet-labels/hooks';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { DependantLabels } from '../schema';

export const useProgramOnSelect = (
  onSelect: (value: LabelValue | null) => void,
  dependentLabels: DependantLabels
): ((value?: WbsLabelValueSearchResult | null) => void) => {
  const { setValue } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const {
    project: autoUpdateProject,
    task: autoUpdateTask
  } = usePCPTAutoUpdateHandlers({
    dependentLabels
  });
  const onProgramSelect = useCallback(
    async (value?: WbsLabelValueSearchResult | null) => {
      const transformedValue = {
        idValue: value?.id,
        numericValue: null,
        stringValue: value?.displayText
      };

      onSelect(value ? transformedValue : null);

      const { program, project, task, billing } = dependentLabels;
      const valueSourceOnReset = isSmartSuggestionEnabled
        ? LabelValueSource.Suggestion
        : LabelValueSource.User;

      const isProgramDifferent = program?.label?.value?.idValue !== value?.id;
      if (isProgramDifferent) {
        setValue(
          `${timesheetLabelsFormField}.${project.index}.label`,
          {
            ...project.label,
            value: null,
            valueSource: valueSourceOnReset,
            acceptedBy: determineLabelAcceptedBy(
              valueSourceOnReset,
              null,
              isSmartSuggestionEnabled
            )
          },
          { shouldValidate: true, shouldDirty: true }
        );

        setValue(
          `${timesheetLabelsFormField}.${task.index}.label`,
          {
            ...task.label,
            value: null,
            valueSource: valueSourceOnReset,
            acceptedBy: determineLabelAcceptedBy(
              valueSourceOnReset,
              null,
              isSmartSuggestionEnabled
            )
          },
          { shouldValidate: true, shouldDirty: true }
        );
        if (billing && billing.label) {
          setValue(
            `${timesheetLabelsFormField}.${billing.index}.label`,
            {
              ...billing.label,
              value: null,
              valueSource: valueSourceOnReset,
              acceptedBy: determineLabelAcceptedBy(
                valueSourceOnReset,
                null,
                isSmartSuggestionEnabled
              )
            },
            { shouldValidate: true, shouldDirty: true }
          );
        }
        if (value) {
          const projectResult = await autoUpdateProject({ program: value?.id });

          if (projectResult) {
            await autoUpdateTask({
              program: value?.id,
              project: projectResult.id
            });
          }
        }
      }
    },
    [
      onSelect,
      dependentLabels,
      setValue,
      timesheetLabelsFormField,
      isSmartSuggestionEnabled,
      autoUpdateProject,
      autoUpdateTask
    ]
  );

  return onProgramSelect;
};

export default useProgramOnSelect;
